import { onMounted, watch } from 'vue'
import { useAnalyticsConfig } from './useAnalyticsConfig'
import { useKlaroConsent } from './useKlaroConsent'

export function useAnalyticsLoader() {
  const { shouldLoadGoogleAnalytics } = useAnalyticsConfig()
  const { hasGoogleAnalyticsConsent } = useKlaroConsent()

  const loadGoogleAnalyticsScripts = () => {
    if (!shouldLoadGoogleAnalytics.value) {
      return
    }

    if (!hasGoogleAnalyticsConsent()) {
      return
    }

    // Sprawdź czy skrypty już istnieją
    const existingScripts = document.querySelectorAll('script[data-name="google-analytics"]')
    if (existingScripts.length > 0) {
      // Jeśli skrypty istnieją, ale gtag nie jest dostępny, usuń je i załaduj ponownie
      if (!window.gtag) {
        existingScripts.forEach(script => script.remove())
      } else {
        return // Skrypty już działają
      }
    }

    // Dodaj pierwszy skrypt (gtag loader)
    const gaScript1 = document.createElement('script')
    gaScript1.type = 'text/javascript'
    gaScript1.setAttribute('data-name', 'google-analytics')
    gaScript1.src = 'https://www.googletagmanager.com/gtag/js?id=G-BV4PRKXHZR'
    document.head.appendChild(gaScript1)

    // Dodaj drugi skrypt (gtag config) po załadowaniu pierwszego
    gaScript1.onload = () => {
      const gaScript2 = document.createElement('script')
      gaScript2.type = 'text/javascript'
      gaScript2.setAttribute('data-name', 'google-analytics')
      gaScript2.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-BV4PRKXHZR');
      `
      document.head.appendChild(gaScript2)
    }
  }

  // Funkcja do sprawdzenia czy gtag jest dostępny
  const checkGtagAvailability = () => {
    return typeof window !== 'undefined' && window.gtag
  }

  // Usuń skrypty Google Analytics
  const removeGoogleAnalyticsScripts = () => {
    const scripts = document.querySelectorAll('script[data-name="google-analytics"]')
    scripts.forEach(script => script.remove())
    
    // Bezpiecznie usuń gtag z window
    try {
      if (window.gtag) {
        delete window.gtag
      }
    } catch (error) {
      // Jeśli nie można usunąć, ustaw na undefined
      window.gtag = undefined
    }
    
    try {
      if (window.dataLayer) {
        delete window.dataLayer
      }
    } catch (error) {
      // Jeśli nie można usunąć, ustaw na undefined
      window.dataLayer = undefined
    }
  }

  // Nasłuchuj zmian zgody w Klaro
  const setupKlaroConsentListener = () => {
    if (!window.klaro) {
      return
    }

    // Nasłuchuj na zdarzenia Klaro
    window.addEventListener('klaro:consent', (event) => {
      const { services } = event.detail
      
      if (services['google-analytics']) {
        // Użytkownik wyraził zgodę na Google Analytics
        setTimeout(() => {
          loadGoogleAnalyticsScripts()
        }, 100)
      } else {
        // Użytkownik odwołał zgodę na Google Analytics
        removeGoogleAnalyticsScripts()
      }
    })

    // Nasłuchuj na zdarzenie gotowości Klaro
    window.addEventListener('klaro:ready', () => {
      const manager = window.klaro.getManager()
      if (manager.consents['google-analytics'] && shouldLoadGoogleAnalytics.value) {
        setTimeout(() => {
          loadGoogleAnalyticsScripts()
        }, 100)
      }
    })
  }

  // Sprawdź i załaduj skrypty
  const checkAndLoadScripts = () => {
    const hasConsent = hasGoogleAnalyticsConsent()
    const shouldLoad = shouldLoadGoogleAnalytics.value
    
    if (hasConsent && shouldLoad) {
      loadGoogleAnalyticsScripts()
    } else {
      removeGoogleAnalyticsScripts()
    }
  }

  onMounted(() => {
    // Sprawdź zapisaną zgodę natychmiast
    checkAndLoadScripts()

    // Dodatkowe sprawdzenie po krótkim opóźnieniu
    setTimeout(() => {
      checkAndLoadScripts()
    }, 500)

    // Ustaw listener na zmiany zgody w Klaro
    setTimeout(() => {
      setupKlaroConsentListener()
    }, 1000)

    // Dodatkowe sprawdzenie co 2 sekundy przez pierwsze 10 sekund
    let checkCount = 0
    const maxChecks = 5
    const checkInterval = setInterval(() => {
      checkCount++
      checkAndLoadScripts()
      
      if (checkCount >= maxChecks) {
        clearInterval(checkInterval)
      }
    }, 2000)
  })

  return {
    loadGoogleAnalyticsScripts,
    removeGoogleAnalyticsScripts,
    checkGtagAvailability
  }
} 